import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Check,
  X,
  Share2,
  Users,
  Save,
  Trash,
  Trash2,
  RotateCcw,
  BookOpen,
  Edit2,
  Archive,
  LogOut,
} from "lucide-react";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { communitiesAPI } from "../services/api";
import { useAuthState } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import useScreenWakeLock from "../hooks/useScreenWakeLock";
import { getOwnedCommunityLimit } from "../constants/contentLimits";
import { writeCommunitiesRouteCache } from "../utils/communitiesRouteCache";
import "./Communities.css";

const Communities = () => {
  useScreenWakeLock(true);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const shareLessonId = searchParams.get("shareLesson");
  const initialRouteState = React.useMemo(
    () => (location.state?.skipFullLoad ? location.state : null),
    [location.state],
  );
  const [ownedCommunities, setOwnedCommunities] = useState(
    initialRouteState?.ownedCommunities || [],
  );
  const [archivedCommunities, setArchivedCommunities] = useState(
    initialRouteState?.archivedCommunities || [],
  );
  const [joinedCommunities, setJoinedCommunities] = useState(
    initialRouteState?.joinedCommunities || [],
  );
  const [invites, setInvites] = useState(initialRouteState?.invites || []);
  const [requests, setRequests] = useState(initialRouteState?.requests || []);
  const [loading, setLoading] = useState(!initialRouteState);
  const [creating, setCreating] = useState(false);
  const [newCommunityName, setNewCommunityName] = useState("");
  const [newCommunityDescription, setNewCommunityDescription] = useState("");
  const [sharingCommunityId, setSharingCommunityId] = useState(null);
  const [inviteLoadingId, setInviteLoadingId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoadingKey, setActionLoadingKey] = useState(null);
  const [editingCommunityId, setEditingCommunityId] = useState(null);
  const [editName, setEditName] = useState("");
  const [communityCodeInput, setCommunityCodeInput] = useState("");
  const [joinByCodeLoading, setJoinByCodeLoading] = useState(false);
  const { addToast } = useToasts();
  const { user } = useAuthState();
  const ownedCommunityLimit = getOwnedCommunityLimit(user);
  const ownedCommunityLimitLabel = Number.isFinite(ownedCommunityLimit)
    ? ownedCommunityLimit
    : "unlimited";
  const hasReachedOwnedCommunityLimit =
    Number.isFinite(ownedCommunityLimit) &&
    ownedCommunities.length >= ownedCommunityLimit;
  const ownedCommunityLimitMessage =
    `Your plan allows ${ownedCommunityLimitLabel} active communit${
      ownedCommunityLimit === 1 ? "y" : "ies"
    }. Archive one to restore this community.`;
  const createCommunityLimitMessage =
    `Your plan allows ${ownedCommunityLimitLabel} active communit${
      ownedCommunityLimit === 1 ? "y" : "ies"
    }.`;

  const loadData = React.useCallback(
    async (showSpinner = true) => {
      try {
        if (showSpinner) setLoading(true);
        const [ownedRes, joinedRes, invitesRes, requestsRes] =
          await Promise.all([
            communitiesAPI.getMine(shareLessonId || ""),
            communitiesAPI.getJoined(),
            communitiesAPI.getInvites(),
            communitiesAPI.getRequests(),
          ]);
        setOwnedCommunities(ownedRes.data.communities || []);
        setArchivedCommunities(ownedRes.data.archivedCommunities || []);
        setJoinedCommunities(joinedRes.data.communities || []);
        setInvites(invitesRes.data.invites || []);
        setRequests(requestsRes.data.requests || []);
        writeCommunitiesRouteCache({
          ownedCommunities: ownedRes.data.communities || [],
          archivedCommunities: ownedRes.data.archivedCommunities || [],
          joinedCommunities: joinedRes.data.communities || [],
          invites: invitesRes.data.invites || [],
          requests: requestsRes.data.requests || [],
        });
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Failed to load communities",
          "error-warning",
        );
      } finally {
        setLoading(false);
      }
    },
    [addToast, shareLessonId],
  );

  useEffect(() => {
    const routeState = location.state;

    if (routeState?.skipFullLoad) {
      const s = routeState;
      if (s.ownedCommunities) setOwnedCommunities(s.ownedCommunities);
      if (s.archivedCommunities) setArchivedCommunities(s.archivedCommunities);
      if (s.joinedCommunities) setJoinedCommunities(s.joinedCommunities);
      if (s.invites) setInvites(s.invites);
      if (s.requests) setRequests(s.requests);
      setLoading(false);
      window.history.replaceState({}, "");
      loadData(false);
    } else {
      loadData();
    }
  }, [loadData, location.state]);

  useEffect(() => {
    if (loading) return;

    writeCommunitiesRouteCache({
      ownedCommunities,
      archivedCommunities,
      joinedCommunities,
      invites,
      requests,
    });
  }, [
    archivedCommunities,
    invites,
    joinedCommunities,
    loading,
    ownedCommunities,
    requests,
  ]);

  const resetCreateForm = () => {
    setNewCommunityName("");
    setNewCommunityDescription("");
    setCreating(false);
  };

  const handleCreateCommunity = async () => {
    if (!newCommunityName.trim()) {
      addToast("Community name is required", "error-warning");
      return;
    }

    if (hasReachedOwnedCommunityLimit) {
      addToast(createCommunityLimitMessage, "error-warning");
      return;
    }

    try {
      const response = await communitiesAPI.createCommunity({
        name: newCommunityName.trim(),
        description: newCommunityDescription.trim(),
      });
      const createdCommunity = response.data?.community;

      if (createdCommunity) {
        setOwnedCommunities((prev) => [
          {
            ...createdCommunity,
            activeMembersCount: createdCommunity.activeMembersCount ?? 1,
            lessonsCount: createdCommunity.lessonsCount ?? 0,
            isShared: false,
          },
          ...prev,
        ]);
      }
      addToast("Community created", "success-info");
      resetCreateForm();
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to create community",
        "error-warning",
      );
    }
  };

  const handleOpenCreateCommunity = () => {
    if (hasReachedOwnedCommunityLimit) {
      addToast(createCommunityLimitMessage, "error-warning");
      return;
    }

    setCreating(true);
  };

  const handleShareToggle = async (community) => {
    if (!shareLessonId) return;

    try {
      setSharingCommunityId(community._id);
      if (community.isShared) {
        await communitiesAPI.unshareLesson(community._id, shareLessonId);
        addToast("Lesson removed from community", "success-info");
      } else {
        await communitiesAPI.shareLesson(community._id, shareLessonId);
        addToast("Lesson shared to community", "success-info");
      }

      setOwnedCommunities((prev) =>
        prev.map((entry) =>
          entry._id === community._id
            ? { ...entry, isShared: !entry.isShared }
            : entry,
        ),
      );
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update lesson share",
        "error-warning",
      );
    } finally {
      setSharingCommunityId(null);
    }
  };

  const handleAcceptInvite = async (membershipId) => {
    try {
      setInviteLoadingId(membershipId);
      await communitiesAPI.acceptInvite(membershipId);
      setInvites((prev) => prev.filter((i) => i._id !== membershipId));
      addToast("Invite accepted", "success-info");
      const joinedRes = await communitiesAPI.getJoined();
      setJoinedCommunities(joinedRes.data.communities || []);
      window.dispatchEvent(new Event("invites-changed"));
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to accept invite",
        "error-warning",
      );
    } finally {
      setInviteLoadingId(null);
    }
  };

  const handleRejectInvite = async (membershipId) => {
    try {
      setInviteLoadingId(membershipId);
      await communitiesAPI.rejectInvite(membershipId);
      setInvites((prev) => prev.filter((i) => i._id !== membershipId));
      addToast("Invite rejected", "success-info");
      window.dispatchEvent(new Event("invites-changed"));
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to reject invite",
        "error-warning",
      );
    } finally {
      setInviteLoadingId(null);
    }
  };

  const handleSaveRename = async (community) => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === community.name) {
      setEditingCommunityId(null);
      return;
    }
    try {
      await communitiesAPI.updateCommunity(community._id, {
        name: trimmed,
        description: community.description,
      });
      setOwnedCommunities((prev) =>
        prev.map((c) =>
          c._id === community._id ? { ...c, name: trimmed } : c,
        ),
      );
      addToast("Community renamed", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to rename",
        "error-warning",
      );
    }
    setEditingCommunityId(null);
  };

  const buildActionKey = (type, id) => `${type}:${id}`;

  const buildCommunitiesRouteState = () => ({
    skipFullLoad: true,
    returnPath: `${location.pathname}${location.search}${location.hash}`,
    ownedCommunities,
    archivedCommunities,
    joinedCommunities,
    invites,
    requests,
  });

  const navigateToCommunity = (community, canManage = false) => {
    navigate(`/communities/${community._id}`, {
      state: {
        fromCommunitiesSnapshot: buildCommunitiesRouteState(),
        prefetchedCommunity: {
          ...community,
          canManage,
        },
      },
    });
  };

  const handleJoinByCode = async () => {
    const trimmedCode = communityCodeInput.trim();
    if (!trimmedCode) {
      addToast("Community code is required", "error-warning");
      return;
    }

    try {
      setJoinByCodeLoading(true);
      const response = await communitiesAPI.joinByCode(trimmedCode);
      addToast(
        response.data?.message || "Community join updated",
        "success-info",
      );
      setCommunityCodeInput("");
      await loadData(false);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Failed to join community by code",
        "error-warning",
      );
    } finally {
      setJoinByCodeLoading(false);
    }
  };

  const handleRequestAction = (action) => {
    setPendingAction(action);
  };

  const handleCancelAction = () => {
    setPendingAction(null);
    setActionLoadingKey(null);
  };

  const isPendingAction = (type, id) =>
    pendingAction?.type === type && pendingAction?.id === id;

  const handleConfirmAction = async (action) => {
    if (!action) return;

    try {
      const actionKey = buildActionKey(action.type, action.id);
      setActionLoadingKey(actionKey);

      if (action.type === "restore") {
        await communitiesAPI.restoreCommunity(action.id);
        setArchivedCommunities((prev) =>
          prev.filter((c) => c._id !== action.id),
        );
        addToast("Community restored", "success-info");
        const ownedRes = await communitiesAPI.getMine(shareLessonId || "");
        setOwnedCommunities(ownedRes.data.communities || []);
        setArchivedCommunities(ownedRes.data.archivedCommunities || []);
      }

      if (action.type === "delete") {
        await communitiesAPI.deleteCommunity(action.id);
        setArchivedCommunities((prev) =>
          prev.filter((c) => c._id !== action.id),
        );
        addToast("Community deleted", "success-info");
      }

      if (action.type === "archive") {
        await communitiesAPI.archiveCommunity(action.id);
        setOwnedCommunities((prev) => prev.filter((c) => c._id !== action.id));
        setArchivedCommunities((prev) => [
          ...prev,
          { ...action.community, isArchived: true },
        ]);
        addToast("Community archived", "success-info");
      }

      if (action.type === "quit") {
        await communitiesAPI.quitCommunity(action.id);
        setJoinedCommunities((prev) => prev.filter((c) => c._id !== action.id));
        addToast("Left community", "success-info");
      }

      if (action.type === "cancel-request") {
        await communitiesAPI.cancelJoinRequest(action.id);
        setRequests((prev) =>
          prev.filter((request) => request._id !== action.id),
        );
        addToast("Join request cancelled", "success-info");
      }

      handleCancelAction();
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update community",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const renderInlineConfirm = (action, confirmVariant = "danger") => {
    const isLoading =
      actionLoadingKey === buildActionKey(action.type, action.id);

    return (
      <div
        className="communities-inline-confirm"
        onClick={(event) => event.stopPropagation()}
      >
        <Button
          className="communities-action-button communities-action-button-round"
          onClick={handleCancelAction}
          disabled={isLoading}
          title="Cancel"
          aria-label="Cancel"
        >
          <X size={14} />
        </Button>
        <Button
          className={`communities-action-button communities-action-button-round ${
            confirmVariant === "success"
              ? "communities-action-button-success"
              : "communities-action-button-danger"
          }`}
          onClick={() => handleConfirmAction(action)}
          disabled={isLoading}
          title="Confirm"
          aria-label="Confirm"
        >
          <Check size={14} />
        </Button>
      </div>
    );
  };

  const breadcrumbs = (
    <Breadcrumbs
      path={[
        { label: "MySpace", path: "/myspace" },
        {
          label: (
            <span className="communities-breadcrumb-label">
              <Users size={14} />
              <span>Communities & Classrooms</span>
            </span>
          ),
          path: null,
        },
      ]}
    />
  );

  if (loading) {
    return (
      <StudyLayout breadcrumbs={breadcrumbs} buttons={<NavigationButtons />}>
        <div className="communities-page">
          <Spinner visible={true} />
        </div>
      </StudyLayout>
    );
  }

  const invitesSection = (
    <section
      className="communities-section communities-invites-section"
      id="my-invites"
    >
      <div className="communities-section-header">
        <div className="communities-section-title-group">
          <h2>My Invites</h2>
          <span>{invites.length}</span>
        </div>
      </div>
      <div className="communities-grid">
        {invites.length === 0 && (
          <div className="communities-square-card communities-square-card-empty">
            No pending invites right now.
          </div>
        )}
        {invites.map((invite) => (
          <div
            className="communities-square-card communities-invite-card"
            key={invite._id}
          >
            <span className="communities-square-card-date">
              {invite.invitedAt?.slice?.(0, 10) || "Invite"}
            </span>
            <h3>{invite.communityName}</h3>
            <p>
              Invited by {invite.invitedBy?.displayName || "Community owner"}.
            </p>
            <div className="communities-square-card-actions">
              <Button
                className="communities-action-button communities-action-button-round communities-action-button-success"
                onClick={() => handleAcceptInvite(invite._id)}
                disabled={inviteLoadingId === invite._id}
                title="Accept invite"
                aria-label="Accept invite"
              >
                <Check size={14} />
              </Button>
              <Button
                className="communities-action-button communities-action-button-round communities-action-button-danger"
                onClick={() => handleRejectInvite(invite._id)}
                disabled={inviteLoadingId === invite._id}
                title="Reject invite"
                aria-label="Reject invite"
              >
                <X size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  const requestsSection = (
    <section className="communities-section communities-invites-section">
      <div className="communities-section-header">
        <div className="communities-section-title-group">
          <h2>My Requests</h2>
          <span>{requests.length}</span>
        </div>
      </div>
      <div className="communities-grid">
        {requests.length === 0 && (
          <div className="communities-square-card communities-square-card-empty">
            No pending requests right now.
          </div>
        )}
        {requests.map((request) => (
          <div
            className="communities-square-card communities-invite-card communities-request-card"
            key={request._id}
          >
            <span className="communities-square-card-date">
              {request.requestedAt?.slice?.(0, 10) || "Request"}
            </span>
            <h3>{request.communityName}</h3>
            <p className="communities-request-card-status">
              Waiting for owner approval.
            </p>
            <div className="communities-request-card-footer">
              <div className="communities-square-card-icons">
                <span>
                  {request.joinMode === "approval" ? "Approval" : "Direct"}
                </span>
              </div>
              <div
                className="communities-action-stack"
                onClick={(event) => event.stopPropagation()}
              >
                {isPendingAction("cancel-request", request._id) ? (
                  renderInlineConfirm(
                    {
                      type: "cancel-request",
                      id: request._id,
                      label: request.communityName,
                    },
                    "danger",
                  )
                ) : (
                  <Button
                    className="communities-action-button communities-action-button-round communities-action-button-danger"
                    onClick={() => {
                      handleRequestAction({
                        type: "cancel-request",
                        id: request._id,
                        label: request.communityName,
                      });
                    }}
                    title="Cancel join request"
                    aria-label="Cancel join request"
                  >
                    <X size={14} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  const joinByCodeSection = (
    <section className="communities-section communities-invites-section">
      <div className="communities-section-header">
        <div className="communities-section-title-group">
          <h2>Join By Code</h2>
        </div>
      </div>
      <div className="communities-grid">
        <div className="communities-square-card communities-join-code-card">
          <input
            type="text"
            value={communityCodeInput}
            onChange={(event) =>
              setCommunityCodeInput(event.target.value.replace(/\s+/g, ""))
            }
            className="communities-input communities-join-code-input"
            placeholder="Community code"
            maxLength="6"
          />
          <Button
            className="communities-action-button communities-action-button-round communities-join-code-button"
            onClick={handleJoinByCode}
            disabled={joinByCodeLoading}
            title={joinByCodeLoading ? "Joining..." : "Join community"}
            aria-label={joinByCodeLoading ? "Joining..." : "Join community"}
          >
            <Users size={15} />
          </Button>
        </div>
      </div>
    </section>
  );

  return (
    <StudyLayout
      breadcrumbs={breadcrumbs}
      buttons={<NavigationButtons />}
    >
      <div className="communities-page">
        {shareLessonId && (
          <div className="communities-share-banner">
            <Share2 size={18} />
            <span>
              Select one or more communities below to share or unshare the
              current lesson.
            </span>
          </div>
        )}

        <div className="communities-primary-sections">
          <section className="communities-section">
            <div className="communities-section-header">
              <div className="communities-section-title-group">
                <h2>Owner</h2>
                <span>{ownedCommunities.length}</span>
                {!creating && (
                  <Button
                    className="communities-action-button communities-action-button-round communities-new-community-button"
                    onClick={handleOpenCreateCommunity}
                    title="New community"
                    aria-label="New community"
                  >
                    <Users size={15} />
                  </Button>
                )}
              </div>
            </div>
            <div className="communities-grid">
              {creating && (
                <div className="communities-square-card communities-create-square-card">
                  <input
                    type="text"
                    value={newCommunityName}
                    onChange={(event) =>
                      setNewCommunityName(event.target.value)
                    }
                    placeholder="Community name"
                    className="communities-input communities-create-card-input"
                    maxLength="80"
                    disabled={hasReachedOwnedCommunityLimit}
                  />
                  <textarea
                    value={newCommunityDescription}
                    onChange={(event) =>
                      setNewCommunityDescription(event.target.value)
                    }
                    placeholder="Short description"
                    className="communities-textarea communities-create-card-textarea"
                    maxLength="240"
                    rows="3"
                    disabled={hasReachedOwnedCommunityLimit}
                  />
                  <div className="communities-square-card-actions">
                    <Button
                      className="communities-action-button communities-action-button-round communities-action-button-success"
                      onClick={handleCreateCommunity}
                      title="Create community"
                      aria-label="Create community"
                      disabled={hasReachedOwnedCommunityLimit}
                    >
                      <Check size={16} />
                    </Button>
                    <Button
                      className="communities-action-button communities-action-button-round communities-action-button-danger"
                      onClick={resetCreateForm}
                      title="Cancel"
                      aria-label="Cancel"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  {hasReachedOwnedCommunityLimit && (
                    <p className="communities-create-limit">
                      Plan limit: {ownedCommunityLimitLabel} active{" "}
                      {ownedCommunityLimit === 1 ? "community" : "communities"}{" "}
                      max.
                    </p>
                  )}
                </div>
              )}
              {ownedCommunities.length === 0 && (
                <div className="communities-square-card communities-square-card-empty">
                  Create your first community to start inviting members.
                </div>
              )}
              {ownedCommunities.map((community) => (
                <div
                  className="communities-square-card communities-card-clickable"
                  key={community._id}
                  onClick={() => navigateToCommunity(community, true)}
                >
                  <div className="communities-square-card-icons">
                    <span>
                      <Users size={14} /> {community.activeMembersCount}
                    </span>
                    <span>
                      <BookOpen size={14} /> {community.lessonsCount}
                    </span>
                  </div>
                  {editingCommunityId === community._id ? (
                    <input
                      className="communities-square-card-rename-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveRename(community);
                        if (e.key === "Escape") setEditingCommunityId(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <h3>{community.name}</h3>
                  )}
                  <div className="communities-square-card-actions communities-owned-card-actions">
                    {editingCommunityId === community._id ? (
                      <div
                        className="communities-action-stack"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="communities-inline-confirm">
                          <Button
                            className="communities-action-button communities-action-button-round"
                            onClick={() => setEditingCommunityId(null)}
                            title="Cancel"
                            aria-label="Cancel"
                          >
                            <X size={14} />
                          </Button>
                          <Button
                            className="communities-action-button communities-action-button-round communities-action-button-success"
                            onClick={() => handleSaveRename(community)}
                            title="Save"
                            aria-label="Save"
                          >
                            <Save size={14} />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button
                          className="communities-action-button communities-action-button-round"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCommunityId(community._id);
                            setEditName(community.name);
                          }}
                          title="Rename"
                          aria-label="Rename"
                        >
                          <Edit2 size={14} />
                        </Button>
                        <div
                          className="communities-action-stack"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {isPendingAction("archive", community._id) ? (
                            renderInlineConfirm(
                              {
                                type: "archive",
                                id: community._id,
                                label: community.name,
                                community,
                              },
                              "danger",
                            )
                          ) : (
                            <Button
                              className="communities-action-button communities-action-button-round communities-action-button-danger"
                              onClick={() => {
                                handleRequestAction({
                                  type: "archive",
                                  id: community._id,
                                  label: community.name,
                                  community,
                                });
                              }}
                              title="Archive"
                              aria-label="Archive"
                            >
                              <Archive size={14} />
                            </Button>
                          )}
                        </div>
                        {shareLessonId && (
                          <Button
                            className={`communities-action-button communities-action-button-round ${
                              community.isShared
                                ? "communities-action-button-danger"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareToggle(community);
                            }}
                            disabled={sharingCommunityId === community._id}
                            title={
                              sharingCommunityId === community._id
                                ? "Working..."
                                : community.isShared
                                  ? "Unshare lesson"
                                  : "Share here"
                            }
                            aria-label={
                              sharingCommunityId === community._id
                                ? "Working..."
                                : community.isShared
                                  ? "Unshare lesson"
                                  : "Share here"
                            }
                          >
                            {community.isShared ? (
                              <Trash2 size={16} />
                            ) : (
                              <Share2 size={16} />
                            )}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section className="communities-section">
            <div className="communities-section-header">
              <div className="communities-section-title-group">
                <h2>Member</h2>
                <span>{joinedCommunities.length}</span>
              </div>
            </div>
            <div className="communities-grid">
              {joinedCommunities.length === 0 && (
                <div className="communities-square-card communities-square-card-empty">
                  No active memberships yet.
                </div>
              )}
              {joinedCommunities.map((community) => (
                <div
                  className={`communities-square-card communities-card-clickable ${
                    community.isPausedByOwner ? "communities-card-paused" : ""
                  }`}
                  key={community._id}
                  onClick={() => navigateToCommunity(community, false)}
                >
                  <div className="communities-square-card-icons">
                    <span>
                      <Users size={14} /> {community.activeMembersCount}
                    </span>
                    <span>
                      <BookOpen size={14} /> {community.lessonsCount}
                    </span>
                  </div>
                  <h3>{community.name}</h3>
                  {community.isPausedByOwner ? (
                    <div className="communities-paused-label">Paused</div>
                  ) : null}
                  <div className="communities-square-card-actions">
                    <div
                      className="communities-action-stack"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {isPendingAction("quit", community._id) ? (
                        renderInlineConfirm(
                          {
                            type: "quit",
                            id: community._id,
                            label: community.name,
                          },
                          "danger",
                        )
                      ) : (
                        <Button
                          className="communities-action-button communities-action-button-round communities-action-button-danger"
                          onClick={() => {
                            handleRequestAction({
                              type: "quit",
                              id: community._id,
                              label: community.name,
                            });
                          }}
                          title="Leave community"
                          aria-label="Leave community"
                        >
                          <LogOut size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          {invitesSection}
          {joinByCodeSection}
          {requestsSection}
          <section className="communities-section">
            <div className="communities-section-header">
              <div className="communities-section-title-group">
                <h2>Archive</h2>
                <span>{archivedCommunities.length}</span>
              </div>
            </div>
            <div className="communities-grid">
              {archivedCommunities.length === 0 && (
                <div className="communities-square-card communities-square-card-empty">
                  No archived communities.
                </div>
              )}
              {archivedCommunities.map((community) => (
                <div
                  className="communities-square-card communities-card-archived"
                  key={community._id}
                >
                  <div className="communities-square-card-icons">
                    <span>
                      <Users size={14} /> {community.activeMembersCount}
                    </span>
                    <span>
                      <BookOpen size={14} /> {community.lessonsCount}
                    </span>
                  </div>
                  <h3>{community.name}</h3>
                  <div className="communities-square-card-actions">
                    <div
                      className="communities-action-stack"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {isPendingAction("restore", community._id) ? (
                        renderInlineConfirm(
                          {
                            type: "restore",
                            id: community._id,
                            label: community.name,
                          },
                          "success",
                        )
                      ) : (
                        <Button
                          className="communities-action-button communities-action-button-round communities-action-button-success"
                          onClick={() => {
                            if (hasReachedOwnedCommunityLimit) {
                              addToast(
                                ownedCommunityLimitMessage,
                                "error-warning",
                              );
                              return;
                            }

                            handleRequestAction({
                              type: "restore",
                              id: community._id,
                              label: community.name,
                            });
                          }}
                          title="Restore community"
                          aria-label="Restore community"
                        >
                          <RotateCcw size={16} />
                        </Button>
                      )}
                    </div>
                    <div
                      className="communities-action-stack"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {isPendingAction("delete", community._id) ? (
                        renderInlineConfirm(
                          {
                            type: "delete",
                            id: community._id,
                            label: community.name,
                          },
                          "danger",
                        )
                      ) : (
                        <Button
                          className="communities-action-button communities-action-button-round communities-action-button-danger"
                          onClick={() =>
                            handleRequestAction({
                              type: "delete",
                              id: community._id,
                              label: community.name,
                            })
                          }
                          title="Delete community"
                          aria-label="Delete community"
                        >
                          <Trash size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                  {hasReachedOwnedCommunityLimit && (
                    <p className="communities-restore-limit">
                      Plan limit: {ownedCommunityLimitLabel} active{" "}
                      {ownedCommunityLimit === 1 ? "community" : "communities"}{" "}
                      max.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>

        <section
          className="communities-about"
          aria-labelledby="communities-about-title"
        >
          <h2 id="communities-about-title">About Communities</h2>
          <p>
            Create classrooms, invite members, join by community code, and
            manage the groups where your lessons are shared.
          </p>
        </section>
      </div>
    </StudyLayout>
  );
};

export default Communities;
